/**
 * FunnelChart — a conversion funnel as centered, tapering horizontal bars.
 * Each stage's bar width is proportional to its value (relative to the top
 * stage), so the funnel visibly narrows as users drop off. Per stage we show
 * the absolute value plus two conversion rates: vs the previous stage (the
 * step-to-step rate) and vs the top of the funnel (overall retention). Intended
 * for ordered drop-off data — sessions → carts → checkouts → orders, or
 * impressions → clicks → leads → sales.
 */

import { moneyFmt } from "./cards.js";

export interface FunnelStep {
  label: string;
  value: number;
}

export function FunnelChart({
  steps,
  money = false,
}: {
  steps: FunnelStep[];
  /** Format values as currency (moneyFmt) rather than counts. */
  money?: boolean;
}): React.ReactElement {
  if (steps.length === 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>No data yet.</div>;
  }

  const fmt = (v: number): string => (money ? moneyFmt(v) : Math.round(v).toLocaleString());
  // The top stage anchors both the widest bar and the overall conversion rate.
  const top = Math.max(1, steps[0]!.value);

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
      {steps.map((s, i) => {
        const prev = i === 0 ? null : steps[i - 1]!.value;
        // Bar width tracks value vs the top stage; opacity steps down per stage
        // (floored at 0.3) so the funnel reads as a single fading shape.
        const widthPct = Math.max(2, (s.value / top) * 100);
        const opacity = Math.max(0.3, 1 - i * 0.12);
        const fromPrev = prev && prev > 0 ? (s.value / prev) * 100 : null;
        const fromTop = (s.value / top) * 100;

        return (
          <div key={`${s.label}-${i}`} style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 3,
                fontSize: 12,
              }}
            >
              <span style={{ color: "var(--body)", fontWeight: 600 }}>{s.label}</span>
              <span style={{ color: "var(--body-faint)" }}>
                {i === 0 ? (
                  <>100%</>
                ) : (
                  <>
                    {fromPrev !== null ? `${Math.round(fromPrev)}%` : "—"}
                    <span style={{ color: "var(--faint)" }}> of prev</span>
                  </>
                )}
              </span>
            </div>

            {/* Track + centered tapering bar. */}
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 30,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: `${widthPct}%`,
                  height: "100%",
                  background: "var(--accent)",
                  opacity,
                  borderRadius: "var(--r-sm, 6px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 4,
                }}
              >
                <span
                  className="dd-num"
                  style={{ color: "var(--surface)", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {fmt(s.value)}
                </span>
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 2, fontSize: 10.5, color: "var(--faint)" }}>
              {Math.round(fromTop)}% of top
            </div>
          </div>
        );
      })}
    </div>
  );
}
